import {
    ApplicationRoleCode,
    CorporationUserRole,
    CurrentUserResponse,
    FavoriteResponse,
    ICurrentUserResponse,
    ProfileChild,
    UserCorporationChild,
} from '@kcftech/prometheus-client';
import { Auth } from 'auth-provider';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { useFavorites } from 'custom_hooks/data_hooks/favorites/useFavorites';
import { useCurrentUserInfo } from 'custom_hooks/data_hooks/user/useCurrentUserInfo';
import { useStoredState } from 'custom_hooks/useStorage';
import { EulaScreen } from 'pages/Login/EulaScreen';
import * as Sentry from '@sentry/react';
import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { Environment } from 'utils/EnvironmentHelper';
import { isUserInternal } from 'utils/RouteHelper';

const defaultPagedDataSourcePageSize = 12;

export interface IUserContext {
    /**
     * Access token for the currently authenticated user.
     */
    // TODO: Remove this token from the context, because there shouldn't be any
    // reason for it, because any API calls will already use it. Most uses are
    // just checking if the user is logged in, which should use the `authenticated`
    // variable instead.
    token?: string | null;
    /**
     * Whether the user is authenticated or not.
     */
    authenticated: boolean;
    /**
     * The info for the current user, if authenticated.
     */
    user?: ICurrentUserResponse;
    /**
     * The favorites for the current user.
     */
    favorites: FavoriteResponse[] | undefined;
    /**
     * Function to update this context with new favorites data.
     */
    updateFavorites: () => Promise<unknown> | unknown;
    /**
     * Reloads all current user data.
     */
    reload: () => void;
    /**
     * The user's selected page size, stored in local storage. If there is
     * no value in local storage, defaults to 12.
     */
    pageSize: number;
    /**
     * Sets the user's selected page size and saves to local storage.
     */
    setPageSize: (pageSize: number) => void;
}

const defaultUserContext: IUserContext = {
    authenticated: false,
    user: new CurrentUserResponse({
        FirstName: 'Test',
        LastName: 'User',
        Email: 'test@kcftech.com',
        ApplicationRole: ApplicationRoleCode.User,
        Corporations: [],
        Locations: [],
        Id: 'd2e66ae1-ba84-49d0-874c-74af496b104f',
        AcceptedEula: true,
        Profile: new ProfileChild(),
        IsApproved: true,
        Tags: [],
    }),
    favorites: undefined,
    updateFavorites: () => {
        /* do nothing */
    },
    reload: () => {
        /* do nothing */
    },
    pageSize: defaultPagedDataSourcePageSize,
    setPageSize: () => {
        /* do nothing */
    },
};
export const UserContext = createContext(defaultUserContext);

export const useUser = () => useContext(UserContext);

/**
 * Returns the local storage key prefix for cognito authentication values
 */
export const getAuthKeyPrefix = (clientId: string, username: string) =>
    `CognitoIdentityServiceProvider.${clientId}.${username}`;
/**
 * Returns the local storage key for the access token
 * @param clientId The Cognito client ID
 * @param username Username / email
 */
export const getAccessTokenKey = (clientId: string, username: string) =>
    `${getAuthKeyPrefix(clientId, username)}.accessToken`;
/**
 * Returns the local storage key for the ID token
 * @param clientId The Cognito client ID
 * @param username Username / email
 */
export const getIdTokenKey = (clientId: string, username: string) =>
    `${getAuthKeyPrefix(clientId, username)}.idToken`;
/**
 * Returns the local storage key for the refresh token
 * @param clientId The Cognito client ID
 * @param username Username / email
 */
export const getRefreshTokenKey = (clientId: string, username: string) =>
    `${getAuthKeyPrefix(clientId, username)}.refreshToken`;
/**
 * Returns the local storage key for the last authenticated username
 * @param clientId The Cognito client ID
 */
export const getLastAuthenticatedUserKey = (clientId: string) =>
    `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`;

/**
 * Gets the locally stored access token for the last logged in user, if it
 * exists. Returns the access token from local storage if it exists, and returns
 * null otherwise.
 */
// The Amplify library does not make it easy to just check if are authenticated
// already. To check for authentication we look at the last authenticated
// username (if it exists), and then see if a corresponding access token for
// that user exists in local storage. If it does, then we should be expect that
// we are authenticated (or at least can be, if the token is expired and needs
// to be refreshed).
export function getLocallyStoredAccessToken(): string | null {
    const lastAuthUsername = localStorage.getItem(
        getLastAuthenticatedUserKey(Environment.cognito.ClientId),
    );
    if (lastAuthUsername) {
        // Check if the last authenticated user also has an existing access
        // token in local storage
        const accessTokenKey = getAccessTokenKey(
            Environment.cognito.ClientId,
            lastAuthUsername,
        );
        return localStorage.getItem(accessTokenKey);
    }
    return null;
}

export const hasAccessToMultipleCorporations = (user?: ICurrentUserResponse) =>
    isUserInternal(user?.ApplicationRole) ||
    (user &&
        user.Corporations.length > 1 &&
        user.Corporations.some(
            (corporationChild: UserCorporationChild) =>
                corporationChild.CorporationUserRole !== CorporationUserRole.None,
        ));

export function UserProvider({ children }: { children: ReactNode }) {
    const existingAccessToken = getLocallyStoredAccessToken();
    const [token, setToken] = useState(existingAccessToken);
    const [authenticated, setAuthenticated] = useState(existingAccessToken !== null);

    const { data: userInfo, refetch: getCurrentInfo } = useCurrentUserInfo();
    const { data: favorites, refetch: getUserFavorites } = useFavorites({
        page: 1,
        pageLimit: 2000,
        orderBy: 'CorporationName, FavoriteName',
    });

    const [pageSize, setPageSize] = useStoredState(
        'pageSize',
        defaultPagedDataSourcePageSize,
    );

    const userContext: IUserContext = useMemo(
        () => ({
            token,
            authenticated,
            user: userInfo,
            favorites: favorites?.Result,
            updateFavorites: () => getUserFavorites(),
            reload: async () => {
                await getCurrentInfo();
                await getUserFavorites();
            },
            pageSize,
            setPageSize,
        }),
        [
            authenticated,
            favorites?.Result,
            getCurrentInfo,
            getUserFavorites,
            pageSize,
            setPageSize,
            token,
            userInfo,
        ],
    );

    useEffect(() => {
        async function authenticateToServer() {
            const accessToken = (await Auth.currentSession())
                .getAccessToken()
                .getJwtToken();
            setToken(accessToken);
            setAuthenticated(true);
        }

        if (authenticated) {
            userContext.reload();
        } else {
            authenticateToServer();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, authenticated]);

    useEffect(() => {
        if (userInfo?.Id) {
            Sentry.setUser({ id: userInfo.Id });
        }
    }, [userInfo?.Id]);

    if (!userContext.user) {
        return <SpinnerArea isLoading />;
    }

    if (!userContext.user.AcceptedEula) {
        return (
            <EulaScreen user={userContext.user} onAccept={() => userContext.reload()} />
        );
    }

    return <UserContext.Provider value={userContext}>{children}</UserContext.Provider>;
}
